// WARNING: This file is for internal use only by DocumentManager.server.ts and DocumentManager.client.ts.
// Do NOT import or use this file directly elsewhere in your codebase.

import { SupabaseClient } from "@supabase/supabase-js";
import { DocumentRow } from "@/lib/types";

export interface DocumentAnalysisResult {
  title?: string;
  doc_type?: DocumentRow["doc_type"];
  description?: string;
  doc_year?: number;
}

export async function getDocument(
  supabase: SupabaseClient,
  documentId: string
): Promise<DocumentRow | null> {
  const { data: document, error } = await supabase
    .from("documents")
    .select("*")
    .eq("id", documentId)
    .single();

  if (error) {
    throw new Error(`Failed to get document: ${error.message}`);
  }

  return document;
}

export async function getDocuments(
  supabase: SupabaseClient,
  tenantId: string,
  options: {
    isPublished?: boolean;
    isArchived?: boolean;
    isPublic?: boolean;
    docType?: string | string[];
    search?: string;
    document_year?: number[];
    excludeCurrentYears?: boolean;
    folderId?: string | null;
  } = {}
): Promise<DocumentRow[]> {
  let query = supabase
    .from("documents")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("uploaded_at", { ascending: false });

  if (options.isPublished !== undefined) {
    query = query.eq("is_published", options.isPublished);
  }

  if (options.isArchived !== undefined) {
    query = query.eq("is_archived", options.isArchived);
  }

  if (options.isPublic !== undefined) {
    query = query.eq("is_public", options.isPublic);
  }

  if (options.docType) {
    if (Array.isArray(options.docType)) {
      // Handle array of document types
      query = query.in("doc_type", options.docType);
    } else {
      // Handle single document type (backward compatibility)
      query = query.eq("doc_type", options.docType);
    }
  }

  if (options.document_year && options.document_year.length > 0) {
    query = query.in("document_year", options.document_year);
  }

  if (options.excludeCurrentYears) {
    const thisYear = new Date().getFullYear();
    const currentYear = thisYear;
    const nextYear = thisYear + 1;
    const prevYear = thisYear - 1;
    query = query.not(
      "document_year",
      "in",
      `(${currentYear},${nextYear},${prevYear})`
    );
  }

  if (options.search) {
    query = query.or(
      `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
    );
  }

  // Handle folder filtering
  if (options.folderId !== undefined) {
    if (options.folderId === null) {
      // For unfiled documents, use .is() for null values
      query = query.is("folder_id", null);
    } else if (options.folderId === "not-found") {
      // For non-existent folders, return no results
      query = query.eq("id", "00000000-0000-0000-0000-000000000000"); // This will never match
    } else {
      // For specific folders, use .eq() for UUID values
      query = query.eq("folder_id", options.folderId);
    }
  }

  const { data: documents, error } = await query;

  if (error) {
    throw new Error(`Failed to get documents: ${error.message}`);
  }

  return documents || [];
}
